/**
 * Which layers a character is made of, and in what order they are drawn for
 * each direction.
 *
 * The order is a rule, not a rendering detail: facing the camera a weapon
 * passes in front of the body, facing away it passes behind, and getting that
 * wrong makes a character look like a sticker. Both the client and the rig
 * compositor read this table so they cannot disagree.
 */
/** Base layers every character has, whatever they wear. */
export const BASE_LAYERS = ['body', 'skin', 'eyes', 'hair'];
/**
 * Slots that are actually drawn: every equipment slot but the companions'.
 * Gloves and boots are, when their template carries a sprite (ADR 0014).
 * Wings (ADR 0053) are a look only: no equipment goes there, only the
 * cosmetic over it.
 */
export const VISIBLE_SLOTS = [
    'boots',
    'chest',
    'gloves',
    'helmet',
    'offhand',
    'weapon',
    'wings',
];
export function isVisibleSlot(slot) {
    return VISIBLE_SLOTS.includes(slot);
}
/**
 * Slots every item of which is drawn: a helmet without a sprite would be a
 * hole on the head. Gauntlets and boots are drawn when their template has a
 * sprite, and otherwise simply leave the hands and feet bare.
 */
export const ALWAYS_DRAWN_SLOTS = ['chest', 'helmet', 'offhand', 'weapon'];
export function isAlwaysDrawnSlot(slot) {
    return ALWAYS_DRAWN_SLOTS.includes(slot);
}
export const LAYERS = [...BASE_LAYERS, ...VISIBLE_SLOTS];
/**
 * Back to front, per direction.
 *
 * Facing north the camera sees the character's back: the held items fall
 * behind the body, and the eyes are not visible at all — which is why `north`
 * simply does not list them, rather than relying on an empty sprite.
 */
const ORDER = {
    // Boots under the plate's skirt; gauntlets over it, the hands hang in front.
    // Wings grow from the back: behind everything but from behind.
    south: [
        'wings',
        'body',
        'skin',
        'eyes',
        'hair',
        'boots',
        'chest',
        'gloves',
        'helmet',
        'offhand',
        'weapon',
    ],
    east: [
        'wings',
        'body',
        'skin',
        'eyes',
        'hair',
        'boots',
        'chest',
        'gloves',
        'helmet',
        'offhand',
        'weapon',
    ],
    west: [
        'wings',
        'body',
        'skin',
        'eyes',
        'hair',
        'boots',
        'chest',
        'gloves',
        'helmet',
        'offhand',
        'weapon',
    ],
    // Seen from behind the arms are behind the torso: what shows of a
    // gauntlet is already only what the torso leaves (build:rig masks it).
    // Long hair falls down the back over the plate (ADR 0077); the wings lie
    // over the back and the hair, under the helm.
    north: [
        'weapon',
        'offhand',
        'body',
        'skin',
        'boots',
        'gloves',
        'chest',
        'hair',
        'wings',
        'helmet',
    ],
};
export function layerOrder(direction) {
    return ORDER[direction];
}
/**
 * Whether a base layer is drawn, given what the character wears.
 *
 * A helmet covers the hair. Hair is drawn from the bare head's silhouette, so
 * it spills past any helmet at the sides and the nape; a fringe escaping a
 * closed helm reads as a mistake rather than as a hairstyle. One rule here
 * rather than a flag per helmet: there is no helmet this should not apply to.
 */
export function isBaseLayerVisible(layer, worn) {
    if (layer !== 'hair')
        return true;
    return (worn.helmet ?? '') === '';
}
/** True when this layer is drawn at all in this direction. */
export function isLayerVisible(layer, direction) {
    return ORDER[direction].includes(layer);
}
/**
 * Draw index of a layer, or -1 when it is hidden in this direction. Callers
 * use it as a depth within the character, so a single container can reorder
 * its children without rebuilding them.
 */
export function layerDepth(layer, direction) {
    return ORDER[direction].indexOf(layer);
}
//# sourceMappingURL=appearance.js.map